import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, CheckCircle2, ShieldCheck, Loader2, Download } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnergyConsent() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/energy/consent/${token}`)
      .then((r) => { setData(r.data); if (r.data.status === "autorizzata") setDone(true); })
      .catch((e) => setErr(e.response?.data?.detail || "Link non valido o scaduto."))
      .finally(() => setLoading(false));
  }, [token]);

  const authorize = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/energy/consent/${token}`, { accept: true });
      setDone(true);
    } catch (e) { setErr(e.response?.data?.detail || "Errore durante l'autorizzazione."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="energy-consent-page">
      <Card className="w-full max-w-lg p-8 glow-border space-y-5">
        <div className="flex items-center justify-between"><Logo size={22} /><Flame className="w-6 h-6 text-primary" /></div>

        {loading && <p className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carico la richiesta…</p>}
        {err && !loading && <p className="text-destructive text-sm" data-testid="consent-error">{err}</p>}

        {data && !loading && (done ? (
          <div className="text-center space-y-3 py-4" data-testid="consent-done">
            <CheckCircle2 className="w-14 h-14 text-teal-500 mx-auto" />
            <h1 className="font-head text-2xl font-bold">Grazie, autorizzazione registrata</h1>
            <p className="text-muted-foreground">Hai autorizzato il passaggio della fornitura <strong>{data.tipo}</strong>. Procederemo con la migrazione al nostro operatore.</p>
            <a href={`${API}/public/energy/consent/${token}/receipt`} target="_blank" rel="noopener noreferrer" data-testid="consent-receipt-link">
              <Button variant="outline" className="gap-2"><Download className="w-4 h-4" /> Scarica ricevuta del consenso</Button>
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h1 className="font-head text-2xl font-bold">Autorizza il passaggio della fornitura</h1>
              <p className="text-sm text-muted-foreground mt-1">Confermi il passaggio (migrazione) della tua fornitura al nostro operatore tramite {data.dealer_name}.</p>
            </div>
            <div className="rounded-lg border border-border p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Intestatario</span><span className="font-medium">{data.nome} {data.cognome}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fornitura</span><span className="font-medium">{data.tipo}</span></div>
              {data.fornitore_attuale && <div className="flex justify-between"><span className="text-muted-foreground">Fornitore attuale</span><span className="font-medium">{data.fornitore_attuale}</span></div>}
              {data.indirizzo && <div className="flex justify-between gap-4"><span className="text-muted-foreground">Indirizzo</span><span className="font-medium text-right">{data.indirizzo}</span></div>}
            </div>
            <p className="text-xs text-muted-foreground flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-teal-500" /> Cliccando "Autorizzo" confermi di essere l'intestatario e di autorizzare il passaggio della fornitura al nuovo operatore.</p>
            <Button className="w-full gap-2" onClick={authorize} disabled={busy} data-testid="consent-authorize">
              <CheckCircle2 className="w-4 h-4" /> {busy ? "Autorizzo…" : "Autorizzo la migrazione"}
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
}
